import React, { useEffect, useMemo } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import cn from "classnames";
import { CloseIcon } from "@/assets/icons";
import Button from "@/ui/Button/Button";
import { useAddToHomeScreenPromptStore } from "@/store/addToHomeScreenPrompt";
import { useAuth } from "@/store/authStore";
import { useUserDataContext } from "@/store/UserProvider";
import { DotsMenuIcon, IosShareIcon } from "./icons";
import { getInstallPromptPlatform, isStandalonePwa } from "./platform";
import { useNativeInstallPrompt } from "./useNativeInstallPrompt";
import cls from "./styles.module.scss";

export default function AddToHomeScreenPrompt() {
  const { t } = useTranslation();
  const { initData, sessionToken } = useAuth();
  const { user } = useUserDataContext();
  const {
    clearInstallPromptEvent,
    installPromptEvent,
  } = useNativeInstallPrompt();
  const { closePrompt, isOpen, showPrompt } = useAddToHomeScreenPromptStore();

  const platform = useMemo(() => getInstallPromptPlatform(), []);
  const isTelegramWebApp = useMemo(() => {
    const hash = window.location.hash || "";

    return hash.includes("tgWebAppPlatform=") || hash.includes("tgWebAppData=");
  }, []);

  const isBrowserSravniDohodUser =
    __SERVICE_NAME__ === "sravni_dohod" && !initData && Boolean(sessionToken);
  const shouldWaitForUser = isBrowserSravniDohodUser && !user;
  const shouldWaitForPromoCodeModal =
    isBrowserSravniDohodUser && user && !user.promocode_modal_shown;

  useEffect(() => {
    if (
      __SERVICE_NAME__ !== "sravni_dohod" ||
      isTelegramWebApp ||
      !platform ||
      shouldWaitForUser ||
      shouldWaitForPromoCodeModal
    ) {
      return;
    }

    if (isStandalonePwa()) return;

    showPrompt();
  }, [
    isTelegramWebApp,
    platform,
    shouldWaitForPromoCodeModal,
    shouldWaitForUser,
    showPrompt,
  ]);

  if (!isOpen || !platform) return null;

  const canInstallFromButton =
    platform.isAndroid && Boolean(installPromptEvent);
  const targetText = platform.isIos
    ? t("addToHomeScreenPrompt.iosTarget")
    : t("addToHomeScreenPrompt.androidTarget");

  const showNativeInstallPrompt = async () => {
    if (!installPromptEvent) return;

    await installPromptEvent.prompt();
    await installPromptEvent.userChoice.catch(() => undefined);

    clearInstallPromptEvent();
    closePrompt();
  };

  return ReactDOM.createPortal(
    <div className={cls.overlay} onClick={closePrompt}>
      <section
        className={cn(cls.bubble, cls[platform.placement])}
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-to-home-title"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className={cls.close}
          aria-label={t("addToHomeScreenPrompt.close")}
          onClick={closePrompt}
        >
          <CloseIcon />
        </button>

        <img
          className={cls.icon}
          src="/assets/icons/icon-192.png"
          alt=""
          aria-hidden="true"
        />

        <h2 id="add-to-home-title">{t("addToHomeScreenPrompt.title")}</h2>
        <p className={cls.subtitle}>{t("addToHomeScreenPrompt.subtitle")}</p>

        {!canInstallFromButton && <div className={cls.divider} />}

        {canInstallFromButton ? (
          <Button
            className={cls.installButton}
            label={t("addToHomeScreenPrompt.androidTarget")}
            onClick={showNativeInstallPrompt}
          />
        ) : (
          <p className={cls.instruction}>
            {t("addToHomeScreenPrompt.instructionBeforeIcon")}{" "}
            <span className={cls.menuIcon}>
              {platform.isIos ? <IosShareIcon /> : <DotsMenuIcon />}
            </span>{" "}
            {t("addToHomeScreenPrompt.instructionAfterIcon", {
              target: targetText,
            })}
          </p>
        )}
      </section>
    </div>,
    document.body
  );
}
